import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button, Divider, Input, Message, Modal } from 'semantic-ui-react';

class AuthenticatorRecovery extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired, request: PropTypes.func.isRequired, disabled: PropTypes.bool,
  };
  state = { passwordOnlyApproval: false, status: 'checking', destination: '', removalAvailable: false, removalSelected: false, open: false, password: '', code: '', pending: false, result: null };
  componentDidMount() { this.alive = true; this.refresh(); }
  componentWillUnmount() { this.alive = false; }
  refresh = async () => {
    if (this.sending) return;
    this.setState({ status: 'checking' });
    let response;
    try { response = await this.props.request({ action: 'status' }); } catch (_) { response = { state: 'unavailable' }; }
    if (this.alive) this.setState({ status: ['ready', 'pending', 'not_registered'].includes(response.state) ? response.state : 'unavailable', destination: response.destination || '', passwordOnlyApproval: response.passwordOnlyApproval === true, removalAvailable: response.removalAvailable === true });
  };
  close = () => {
    if (this.sending) return;
    this.setState({ open: false, password: '', code: '', result: null });
    this.refresh();
  };
  approve = async () => {
    if (this.sending || this.props.disabled || this.state.status !== 'ready' || !this.state.password || (!this.state.passwordOnlyApproval && !/^\d{6}$/.test(this.state.code)) || this.state.result) return;
    if (!window.crypto || !window.crypto.getRandomValues) return;
    const bytes = new Uint8Array(16); window.crypto.getRandomValues(bytes);
    const requestId = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    this.sending = true;
    const input = { action: this.state.removalSelected ? 'approve-removal' : 'approve', requestId, password: this.state.password, code: this.state.passwordOnlyApproval ? '' : this.state.code };
    this.setState({ pending: true });
    let result;
    try { result = await this.props.request(input); } catch (_) { result = { state: 'uncertain' }; }
    this.sending = false;
    if (this.alive) this.setState({ pending: false, password: '', code: '', result }, () => { if (this.resultPanel) this.resultPanel.focus(); });
  };
  render() {
    const { t, disabled } = this.props;
    const {
      status, destination, passwordOnlyApproval, removalAvailable, removalSelected,
      open, password, code, pending, result,
    } = this.state;
    const messages = {
      invalid_password: 'Your HQ password was not accepted. Use the password for this environment.',
      approved: removalSelected
        ? 'Removal approved. Ask the user to sign in and verify their email.'
        : 'Recovery approved. Ask the user to sign in and check their email for a code.',
      uncertain: 'The result is unconfirmed. Check recovery status before trying again.',
      unavailable: 'Recovery could not be approved. Check your details and recovery access.',
    };
    return (<section style={{ paddingBottom: 8 }} aria-label={t('Authenticator recovery')} tabIndex={-1} ref={(ref) => { this.section = ref; }}>
      <Divider style={{ margin: '12px 0' }} />
      <div style={{ fontWeight: 700, marginBottom: 8 }}>{t('Authenticator recovery')}</div>
      <Button type="button" fluid disabled={disabled || pending || status !== 'ready'} onClick={(e) => { this.reviewTrigger = e.currentTarget; this.setState({ open: true, removalSelected: removalAvailable }); }}>{t(removalAvailable ? 'Remove authenticator' : 'Recover authenticator')}</Button>
      {removalAvailable && <Button type="button" fluid style={{ marginTop: 8 }} disabled={disabled || pending || status !== 'ready'} onClick={(e) => { this.reviewTrigger = e.currentTarget; this.setState({ open: true, removalSelected: false }); }}>{t('Recover authenticator')}</Button>}
      {['checking', 'pending', 'not_registered', 'unavailable'].includes(status) && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8, marginTop: 12 }}>
          {status === 'checking' && <p style={{ margin: 0 }} role="status">{t('Checking recovery access…')}</p>}
          {status === 'pending' && <p style={{ margin: 0 }}>{t('Recovery is already in progress. Ask the user to sign in.')}</p>}
          {status === 'not_registered' && <p style={{ margin: 0 }}>{t('No authenticator is registered.')}</p>}
          {status === 'unavailable' && <p style={{ margin: 0 }}>{t('Recovery is not available for this account.')}</p>}
          {status === 'unavailable' && <Button type="button" basic size="mini" style={{ margin: 0 }} disabled={pending} onClick={this.refresh}>{t('Check again')}</Button>}
        </div>
      )}
      <Modal
        size="tiny" open={open} onClose={this.close} closeOnEscape={!pending} closeOnDimmerClick={!pending} role="dialog" {...{ 'aria-modal': true }} aria-labelledby="authenticator-recovery-title"
        onUnmount={() => {
          if (this.reviewTrigger && this.reviewTrigger.isConnected &&
            !this.reviewTrigger.disabled) this.reviewTrigger.focus();
          else if (this.section) this.section.focus();
        }}
        onKeyDown={(e) => {
          if (e.key !== 'Tab') return;
          const items = Array.from(e.currentTarget.querySelectorAll('button:not(:disabled), input:not(:disabled), [tabindex="0"]'));
          if (!items.length) { e.preventDefault(); return; }
          const first = items[0]; const last = items[items.length - 1];
          const fromFirst = document.activeElement === first ||
            document.activeElement === this.resultPanel;
          if (e.shiftKey && fromFirst) {
            e.preventDefault(); last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault(); first.focus();
          }
        }}
      >
        <Modal.Header id="authenticator-recovery-title">{t(removalSelected ? 'Remove authenticator' : 'Recover authenticator')}</Modal.Header>
        <Modal.Content>
          {!result && <React.Fragment>
            <p>{t(removalSelected ? 'This company has opted out of 2FA. The user verifies their password and email, then continues without an authenticator. Existing sessions are signed out.' : 'The user will verify their email and set up a new authenticator when they sign in.')}</p>
            <p>{t('Recovery email')}: <strong>{destination}</strong></p>
            <fieldset disabled={pending || disabled} style={{ border: 0, padding: 0, margin: 0 }}>
              <label htmlFor="hq-recovery-password" style={{ display: 'block', marginBottom: 12 }}>{t('Your password')}<Input id="hq-recovery-password" onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} fluid type="password" autoComplete="current-password" value={password} onChange={(_, data) => this.setState({ password: data.value })} /></label>
              {!passwordOnlyApproval && <label htmlFor="hq-recovery-code" style={{ display: 'block' }}>{t('Your authenticator code')}<Input id="hq-recovery-code" onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} fluid inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(_, data) => this.setState({ code: data.value.replace(/\D/g, '') })} /></label>}
            </fieldset>
          </React.Fragment>}
          <div aria-live="polite" tabIndex={-1} ref={(ref) => { this.resultPanel = ref; }}>
            {pending && <p role="status">{t('Approving recovery…')}</p>}
            {result && <Message warning={result.state !== 'approved'} style={{ display: 'block' }}>{t(messages[result.state] || messages.uncertain)}</Message>}
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button type="button" autoFocus disabled={pending} onClick={this.close}>{t(result ? 'Close' : 'Cancel')}</Button>
          {!result && <Button type="button" primary loading={pending} disabled={disabled || pending || !password || (!passwordOnlyApproval && !/^\d{6}$/.test(code))} onClick={this.approve}>{t(removalSelected ? 'Approve removal' : 'Approve recovery')}</Button>}
        </Modal.Actions>
      </Modal>
    </section>);
  }
}
export default AuthenticatorRecovery;



// WEBPACK FOOTER //
// ./src/components/AuthenticatorRecovery.jsx